import { useState } from "react";
import { Modal, Button, Select, Row, Col, Badge } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import filtericon from "assets/filterTaskicon.svg";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import DatePicker from "antd/lib/date-picker";
import COLORS from "common/Colors";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { getUserList } from "redux/selectors";
import { connect, useSelector } from "react-redux";


const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Styled ─────────────────────────────────────────────────────────────────────

const FilterModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-header {
    padding: 18px 24px 14px;
    border-bottom: 1px solid #f0f0f0;
    margin: 0;
  }
  .ant-modal-title {
    font-size: 15px;
    font-weight: 600;
    color: #111;
  }
  .ant-modal-close {
    top: 14px;
    right: 16px;
  }
  .ant-modal-body {
    padding: 20px 24px 10px;
  }
  .ant-modal-footer {
    padding: 12px 24px 18px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #555;
  display: block;
  margin-bottom: 6px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 20px !important;
    min-height: 36px !important;
    height: auto !important;
    align-items: center;
    display: flex !important;
    font-size: 13px;
    flex-wrap: wrap;
    padding: 2px 8px !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #e2e2e8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  border-radius: 20px !important;
  height: 36px;
  font-size: 13px;
  .ant-picker-input > input {
    font-size: 13px;
  }
  .ant-picker-range-separator {
    padding: 0 8px;
    color: #999;
  }
  &:hover,
  &.ant-picker-focused {
    border-color: #e2e2e8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const ClearBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  border: 1.5px solid #000 !important;
  color: #000 !important;
  background: white;
  &:hover,
  &:focus {
    border-color: #000 !important;
    color: #000 !important;
  }
`;

const ApplyBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  background-color: #c2fae5;
  border-color: #c2fae5;
  color: #000;
  &:hover,
  &:focus {
    background-color: #c2fae5 !important;
    border-color: #c2fae5 !important;
    color: #000 !important;
  }
`;

const FilterIconBtn = styled(Button)`
  border-radius: 50%;
  width: 36px;
  margin-bottom: 12px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #c2fae5;
  background-color: ${COLORS.SECONDARY_GREEN};
  color: #000;
  padding: 0;
  &:hover,
  &:focus {
    border-color: ${COLORS.SECONDARY_GREEN} !important;
    color: #000 !important;
    background-color: ${COLORS.SECONDARY_GREEN};
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

// ── Helpers ────────────────────────────────────────────────────────────────────

const countActiveFilters = (filters) => {
  const { dueDateFrom, dueDateTo, createdDateFrom, createdDateTo, ...rest } = filters;
  const restCount = Object.values(rest).filter((v) => {
    if (v === null || v === undefined || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }).length;
  const dueDateCount = dueDateFrom || dueDateTo ? 1 : 0;
  const createdDateCount = createdDateFrom || createdDateTo ? 1 : 0;
  return restCount + dueDateCount + createdDateCount;
};

// Resolve a display name for a "created by" userid by looking it up
// against the full userList. Guards against userList being undefined
// (e.g. before the redux selector has populated it).
const resolveUserLabel = (userid, userList) => {
  const list = userList ?? [];
  const match = list.find((u) => u.userid === userid);

  return {
    value: userid,
    label: match?.fullname || userid,
  };
};

// ── Component ──────────────────────────────────────────────────────────────────

const TaskFilterModal = ({
  taskTypeOptions = [],   // string[]
  matterOptions = [],     // { matterid, mattername }[]
  createdByOptions = [],  // userid[] — ids of users who've created tasks
  userList = [],          // full user directory: { userid, firstname, ... }[] — used to resolve names
  onApply,
  onClear,
  loading = false,
  hideMatter = false,     // hide Matter filter when scoped to a single matter
}) => {
  const emptyFilters = {
    tasktype: [],
    matterid: [],
    dueDateFrom: null,
    dueDateTo: null,
    createdby: [],
    createdDateFrom: null,
    createdDateTo: null,
    isprivate: null,
  };

  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const set = (key) => (val) =>
    setFilters((prev) => ({ ...prev, [key]: val ?? null }));

  const handleDueDateRange = (dates) => {
    const [from, to] = dates ?? [];
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: from?.isValid() ? from.startOf("day").toISOString() : null,
      dueDateTo: to?.isValid() ? to.endOf("day").toISOString() : null,
    }));
  };

  const handleCreatedDateRange = (dates) => {
    const [from, to] = dates ?? [];
    setFilters((prev) => ({
      ...prev,
      createdDateFrom: from?.isValid() ? from.startOf("day").toISOString() : null,
      createdDateTo: to?.isValid() ? to.endOf("day").toISOString() : null,
    }));
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    onClear?.();
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    onApply?.(filters);
    setOpen(false);
  };

  const handleCancel = () => {
    setFilters(appliedFilters); // discard uncommitted edits
    setOpen(false);
  };

  const badgeCount = countActiveFilters(appliedFilters);
  const liveCount = countActiveFilters(filters);

  return (
    <>
      {/* ── Trigger button ─────────────────────────────────────────────── */}
      <Badge count={badgeCount} size="small" color="red" offset={[-2, 2]}>
        <FilterIconBtn
          icon={<img src={filtericon} alt="Filter" />}
          onClick={() => setOpen(true)}
        />
      </Badge>

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      <FilterModal
        open={open}
        onCancel={handleCancel}
        title={`Filter${liveCount > 0 ? ` (${liveCount})` : ""}`}
        width={900}
        centered
        destroyOnClose={false}
        closeIcon={<CloseOutlined style={{ fontSize: 13 }} />}
        footer={[
          <ButtonOutlined key="clear" onClick={handleClear}>
            Clear All
          </ButtonOutlined>,
          <ButtonFilled key="apply" onClick={handleApply} loading={loading}>
            Apply Filter
          </ButtonFilled>,
        ]}
      >
        <Row gutter={[16, 0]}>
          {/* Type */}
          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Type</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.tasktype}
                onChange={set("tasktype")}
                allowClear
                maxTagCount="responsive"
              >
                {taskTypeOptions.map((t) => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </Col>

          {/* Matter — hidden when scoped to a specific matter */}
          {!hideMatter && (
            <Col span={6}>
              <FieldGroup>
                <FieldLabel>Matter</FieldLabel>
                <StyledSelect
                  mode="multiple"
                  placeholder="All"
                  value={filters.matterid}
                  onChange={set("matterid")}
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {matterOptions.map((m) => (
                    <Option key={m.matterid} value={m.matterid}>
                      {m.mattername}
                    </Option>
                  ))}
                </StyledSelect>
              </FieldGroup>
            </Col>
          )}

          {/* Created by — displays the user's name, value stored is the userid */}
          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Created by</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.createdby}
                onChange={set("createdby")}
                allowClear
                showSearch
                maxTagCount="responsive"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {createdByOptions.map((userid) => {
                  const { value, label } = resolveUserLabel(userid, userList);
                  return (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  );
                })}
              </StyledSelect>
            </FieldGroup>
          </Col>

          {/* Task Privacy */}
          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Task Privacy</FieldLabel>
              <StyledSelect
                placeholder="All"
                value={
                  filters.isprivate !== null && filters.isprivate !== undefined
                    ? filters.isprivate
                    : undefined
                }
                onChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    isprivate: val !== undefined ? val : null,
                  }))
                }
                allowClear
              >
                <Option value={true}>Private</Option>
                <Option value={false}>Public</Option>
              </StyledSelect>
            </FieldGroup>
          </Col>

          {/* Due Date Range */}
          <Col span={12}>
            <FieldGroup>
              <FieldLabel>Due Date Range</FieldLabel>
              <StyledRangePicker
                placeholder={["From", "To"]}
                format="DD-MM-YYYY"
                value={
                  filters.dueDateFrom && filters.dueDateTo
                    ? [dayjs(filters.dueDateFrom), dayjs(filters.dueDateTo)]
                    : undefined
                }
                onChange={handleDueDateRange}
                separator={<span style={{ color: "#999", fontSize: 14 }}>↔</span>}
              />
            </FieldGroup>
          </Col>

          {/* Created Date Range */}
          <Col span={12}>
            <FieldGroup>
              <FieldLabel>Created Date Range</FieldLabel>
              <StyledRangePicker
                placeholder={["From", "To"]}
                format="DD-MM-YYYY"
                value={
                  filters.createdDateFrom && filters.createdDateTo
                    ? [dayjs(filters.createdDateFrom), dayjs(filters.createdDateTo)]
                    : undefined
                }
                onChange={handleCreatedDateRange}
                separator={<span style={{ color: "#999", fontSize: 14 }}>↔</span>}
              />
            </FieldGroup>
          </Col>
        </Row>
      </FilterModal>
    </>
  );
};


const mapStateToProps = (state) => ({
  userList: getUserList(state),

});

export default connect(mapStateToProps)(TaskFilterModal);